/**
 * Constantes de dominio compartilhadas entre Worker e PWA.
 * Fonte unica de verdade — nao duplicar valores no front nem no back.
 */

export const API_PREFIX = '/api/v1' as const;

/** Fuso padrao da equipe (SoEnergy Brasil — Belem/PA). */
export const DEFAULT_TIMEZONE = 'America/Belem' as const;

// ---------------------------------------------------------------- prioridade

export const PRIORITY = {
  NONE: 0,
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
} as const;

export type Priority = (typeof PRIORITY)[keyof typeof PRIORITY];

export const PRIORITY_LABEL_PT: Record<Priority, string> = {
  [PRIORITY.NONE]: 'Nenhuma',
  [PRIORITY.LOW]: 'Baixa',
  [PRIORITY.MEDIUM]: 'Media',
  [PRIORITY.HIGH]: 'Alta',
};

/**
 * Limiar de "importante" na matriz de Eisenhower (spec 4.1):
 * importante := priority >= 2.
 */
export const IMPORTANT_PRIORITY_THRESHOLD = PRIORITY.MEDIUM;

// ----------------------------------------------------------------- quadrante

export const QUADRANT = {
  URGENT_IMPORTANT: 1,
  NOT_URGENT_IMPORTANT: 2,
  URGENT_NOT_IMPORTANT: 3,
  NEITHER: 4,
} as const;

export type Quadrant = (typeof QUADRANT)[keyof typeof QUADRANT];

/**
 * Janela de urgencia (spec 4.1): urgente := due_at != null AND due_at <= agora + 48h.
 * Vencidas continuam urgentes porque due_at <= agora + 48h tambem vale para o passado.
 */
export const URGENT_WINDOW_MS = 48 * 60 * 60 * 1000;

export const QUADRANT_LABEL_PT: Record<Quadrant, string> = {
  [QUADRANT.URGENT_IMPORTANT]: 'Urgente e importante',
  [QUADRANT.NOT_URGENT_IMPORTANT]: 'Nao urgente e importante',
  [QUADRANT.URGENT_NOT_IMPORTANT]: 'Urgente e nao importante',
  [QUADRANT.NEITHER]: 'Nem urgente, nem importante',
};

/** Estado vazio instrutivo, nao decorativo (spec 9.2). */
export const QUADRANT_EMPTY_COPY_PT: Record<Quadrant, string> = {
  [QUADRANT.URGENT_IMPORTANT]: 'Nada urgente agora — bom sinal.',
  [QUADRANT.NOT_URGENT_IMPORTANT]: 'Planeje algo aqui: e o quadrante que evita incendios.',
  [QUADRANT.URGENT_NOT_IMPORTANT]: 'Delegue algo aqui.',
  [QUADRANT.NEITHER]: 'Nada acumulado. Elimine o que nao merece voltar.',
};

// -------------------------------------------------------------------- status

export const TASK_STATUS = ['open', 'done', 'cancelled'] as const;
export type TaskStatus = (typeof TASK_STATUS)[number];

// ------------------------------------------------------------------ pomodoro

export const POMODORO_MODES = ['focus', 'short_break', 'long_break', 'stopwatch'] as const;
export type PomodoroMode = (typeof POMODORO_MODES)[number];

export const POMODORO_DEFAULTS = {
  focusSeconds: 25 * 60,
  shortBreakSeconds: 5 * 60,
  longBreakSeconds: 15 * 60,
  cyclesBeforeLongBreak: 4,
} as const;

// -------------------------------------------------------------------- habitos

export const HABIT_GOAL_TYPES = ['boolean', 'count'] as const;
export type HabitGoalType = (typeof HABIT_GOAL_TYPES)[number];

/** Bitmask dom..sab; 127 = todos os dias. */
export const ALL_DAYS_MASK = 127;

// ---------------------------------------------------------------- countdowns

export const COUNTDOWN_REPEATS = ['none', 'weekly', 'monthly', 'yearly'] as const;
export type CountdownRepeat = (typeof COUNTDOWN_REPEATS)[number];

// ---------------------------------------------------------------------- misc

export const USER_ROLES = ['admin', 'member'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Retencao da lixeira antes da purga definitiva (spec 3). */
export const TRASH_RETENTION_DAYS = 30;

// ----------------------------------------------------------- teto de leitura

/**
 * Quantas tarefas `GET /tasks` devolve por vez.
 *
 * Ate 19/08/2026 a consulta nao tinha LIMIT nenhum: a visao "Todas" traz as
 * concluidas e o historico de conclusoes nao e purgado (so a lixeira e, em 30
 * dias), entao a resposta crescia para sempre. No volume de hoje isso e
 * irrelevante; o teto existe para que deixe de ser um problema que aparece
 * sozinho, num dia qualquer, na forma de uma tela que demora a abrir.
 *
 * O valor e alto de proposito — ele e uma REDE, nao paginacao de verdade. A
 * tela avisa quando bate no teto e oferece carregar o proximo bloco.
 */
export const TASK_PAGE_SIZE = 300;

/**
 * Recorte padrao das concluidas nas telas que as mostram (visao "Todas" e a
 * gaveta de cada quadrante da matriz).
 *
 * Sem ele as duas traziam tudo que ja foi concluido, para sempre. Trinta dias
 * e a mesma janela da lixeira: e o horizonte em que "o que eu fiz" ainda e uma
 * pergunta sobre o presente. Quem procura algo mais antigo usa a busca, que
 * NAO aplica recorte nenhum.
 */
export const DONE_WINDOW_DAYS = 30;

/** Janela do toast "Desfazer" (spec 10.3). */
export const UNDO_WINDOW_MS = 8_000;

/** Presets de adiamento (spec 4.3). */
export const SNOOZE_PRESETS = [
  'one_hour',
  'this_evening',
  'tomorrow',
  'next_week',
] as const;
export type SnoozePreset = (typeof SNOOZE_PRESETS)[number];

export const SNOOZE_PRESET_LABEL_PT: Record<SnoozePreset, string> = {
  one_hour: '+1 hora',
  this_evening: 'Hoje a noite',
  tomorrow: 'Amanha',
  next_week: 'Proxima semana',
};

/** Horarios fixos dos presets, no fuso do usuario. */
export const SNOOZE_ANCHOR_HOURS = {
  thisEveningHour: 19,
  tomorrowHour: 9,
  nextWeekHour: 9,
} as const;
